import type { EntityOptions } from 'models';
import { Form, Loading } from 'ui';
import { useOrganizationsInfiniteQuery } from 'features/organizations';
import { ChannelsOrganizationSelect } from './channels-organization-select';
import { CustomChannels } from './custom-channels';
import { DefaultChannels } from './default-channels';
import { useGetEmptyDescription } from './hooks';
import type { ChannelScope } from './models';

type ChannelsFormData = {
  organizationId: string;
};

type P = {
  getPathToEntityDetail: (id: string, options: EntityOptions) => string;
  navigateToChannelDetail: (id: string) => Promise<void>;
  navigateToOrganizationChannel: (id: string, scope: ChannelScope) => Promise<void>;
};

const Channels = ({ getPathToEntityDetail, navigateToChannelDetail, navigateToOrganizationChannel }: P) => {
  const [form] = Form.useForm();
  const { data: organizations, isLoading: isLoadingOrganizations } = useOrganizationsInfiniteQuery({
    filter: { order: 'channels', scope: 'joined' },
  });
  const allOrganizations = organizations?.pages.flatMap((organizationsPaginated) => organizationsPaginated.data);

  const getEmptyDescription = useGetEmptyDescription();

  if (isLoadingOrganizations || !allOrganizations) {
    return <Loading />;
  }

  return (
    <Form<ChannelsFormData> form={form} initialValues={{ organizationId: allOrganizations[0]?.id }}>
      <Form.Item<ChannelsFormData> name="organizationId">
        <ChannelsOrganizationSelect organizations={allOrganizations} />
      </Form.Item>

      <Form.Item<ChannelsFormData> dependencies={['organizationId']} noStyle>
        {({ getFieldsValue }) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: fix
          const { organizationId }: ChannelsFormData = getFieldsValue(true);

          const organization = allOrganizations.find(
            (channelOrganization) => channelOrganization.id === organizationId,
          );

          const isDefaultOrganization = organization?.isDefault;

          if (organization?.customChannels) {
            return (
              <CustomChannels
                emptyDescription={getEmptyDescription({ isDefaultOrganization })}
                getPathToEntityDetail={getPathToEntityDetail}
                navigateToChannelDetail={navigateToChannelDetail}
                organizationId={organizationId}
              />
            );
          }

          const defaultOrganizationId = isDefaultOrganization ? undefined : organizationId;

          return (
            <DefaultChannels
              emptyDescription={getEmptyDescription({ isDefaultOrganization })}
              getPathToEntityDetail={getPathToEntityDetail}
              navigateToOrganizationChannel={navigateToOrganizationChannel}
              organizationId={organizationId}
              organizationIdForRequests={defaultOrganizationId}
            />
          );
        }}
      </Form.Item>
    </Form>
  );
};

export default Channels;
